<script setup lang="ts">
import { type ComponentPublicInstance, nextTick, ref, useTemplateRef, watch } from 'vue'
import { randomFloatFromInterval } from '@/helpers/utils'
import CoinIconWebp from '@/components/icons/CoinIconWebp.vue'

const props = defineProps<{
	show: boolean
}>()

const coinsRef = useTemplateRef<ComponentPublicInstance[], string>('coinItems')

const coinsContainer = ref<HTMLElement | null>()
const totalCoins = 140
watch(() => props.show, show => {
  if (!show) {
    return
  }

  nextTick(() => {
    if (!coinsContainer.value || !coinsRef.value) return

    const initialRange = 120 // Initial range in vw (can be larger than screen width)
    const narrowingScale = 1.1 // Scale factor to reduce narrowing even more
    const delayMax = 1 // Maximum delay
    const coins = coinsRef.value
    const totalCoins = coins.length

    const bottomLayerCount = 3 // Number of coins in the bottom layer to have a larger delay

    coins.forEach((coinElement: ComponentPublicInstance, index: number) => {
      const htmlElement = coinElement.$el
      const isLeft = Math.random() < 0.5 // Randomly decide if the coin is on the left or right
      const initialPosition = isLeft
        ? 50 - Math.random() * (initialRange / 2) // Left side
        : 50 + Math.random() * (initialRange / 2) // Right side

      const coinHtmlElement = htmlElement as HTMLElement

      let delay
      if (index >= totalCoins - bottomLayerCount) {
        // Apply larger delay for the bottom layer
        delay = randomFloatFromInterval(0.5, delayMax)
      } else {
        delay = randomFloatFromInterval(0.1, delayMax - 0.15)
      }

      coinHtmlElement.style.left = `${initialPosition}vw`
      coinHtmlElement.style.animationDelay = `${delay}s`

      // Set a random duration for the flying effect
      const duration = randomFloatFromInterval(4, 6) + 's'
      coinHtmlElement.style.setProperty('--duration', duration)

      // Calculate narrowing based on delay
      const narrowing = isLeft
        ? (50 - initialPosition) * narrowingScale * (1 - delay / delayMax) // Left side moves to the center
        : -(initialPosition - 50) * narrowingScale * (1 - delay / delayMax) // Right side moves to the center

      coinHtmlElement.style.setProperty('--narrowing', `${narrowing}`)
    })
  })
})
</script>

<template>
  <div
    ref="coinsContainer"
    class="coins-container"
    :key="show.toString()"
    :class="{'z-[-1]': !show, 'pointer-events-none z-[10000]': show}"
  >
    <CoinIconWebp
      class="coin"
      v-for="i in totalCoins"
      :key="i"
      ref="coinItems"
    />
  </div>
</template>

<style lang="scss">
.coins-container {
	@apply fixed left-0 bottom-0 z-[1001] overflow-hidden h-[90vh] w-screen;
	will-change: transform, opacity;
}

.coin {
	@apply flex items-center absolute w-auto h-6 max-h-[24px] -bottom-[50px] pointer-events-none;
	animation: fly 0.65s linear;
	will-change: transform, opacity;
}

@keyframes fly {
	0% {
		transform: translateY(0) translateX(0);
		opacity: 1;
	}
	60% {
		opacity: 1;
	}
	100% {
		transform: translateY(-90vh) translateX(calc(var(--narrowing) * 1vw));
		opacity: 0;
	}
}
</style>
